import type { ReactElement } from 'react';
import { FilterController, FilterList } from '@amzn/stencil-react-components/filtering';

type FilterChangeValue = string | number | (string | number)[] | undefined;

export interface UseLocationFilterElementParams {
  /** Available location codes (from `useLocationFilter().options`). */
  options: readonly string[];
  /** Currently-applied locations (multi-select). */
  value: string[];
  /** Called when the selection changes. */
  onChange: (value: FilterChangeValue) => void;
  /** Feature-specific chip title. */
  titleText: string;
  /** Feature-specific text for the FilterList reset button. */
  resetButtonText: string;
  /** Feature-specific title shown inside the FilterList flyout panel. */
  listTitleText: string;
}

/**
 * Returns the locations `FilterController` element for a list-page
 * FilterBar. Exposed as a hook (not a component) because Stencil's
 * FilterBar iterates its children with `React.Children.forEach` and
 * only recognises children whose element type is `FilterController`
 * itself — a wrapping component would be silently skipped and the
 * chip would never render.
 *
 * The caller invokes the hook and places the returned element directly
 * as a child of `<FilterBar>`:
 *
 * ```tsx
 * const locationFilter = useLocationFilterElement({ ... });
 * return (
 *   <FilterBar>
 *     ...
 *     {locationFilter}
 *   </FilterBar>
 * );
 * ```
 *
 * Both list bars pull options from `useLocationFilter()` (the user's
 * saved locations for the active BU) and both support the same
 * searchable multi-select UX — sharing the FilterController + FilterList
 * JSX and prop wiring keeps that in one place.
 */
// eslint-disable-next-line @eslint-react/hooks-extra/no-unnecessary-use-prefix -- the `use` prefix mirrors useDateRangeFilterElement and signals that the returned element must be placed directly as a FilterBar child (see docblock).
export function useLocationFilterElement({
  options,
  value,
  onChange,
  titleText,
  resetButtonText,
  listTitleText,
}: UseLocationFilterElementParams): ReactElement {
  return (
    <FilterController name="locations" titleText={titleText}>
      <FilterList
        isMulti
        isSearchable
        onFilterChange={onChange}
        options={options.map((loc) => ({ label: loc, value: loc }))}
        resetButtonText={resetButtonText}
        titleText={listTitleText}
        value={value}
      />
    </FilterController>
  );
}
